import LinkCard from '@/modules/document/models/card/link-card.ts';
import { defineStore } from 'pinia';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import type DocumentCard from '@/modules/document/models/card/document-card.ts';
import type { ClientCard } from '@/modules/shared/feature-bridges.ts';
import type BaseFile from '@/modules/common/models/file/base-file.ts';
import ExternalFile from '@/modules/common/models/file/external-file.ts';
import { mergeUniqueFiles } from '@/modules/ui/utils/merge-unique-files.ts';
import { DeadlineType } from '@/modules/common/enums/deadline-type.ts';
import { DateTime } from 'luxon';
import { useUserStore } from '@/modules/core/stores/user.store.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { RouteType } from '@/modules/common/enums/route-type.ts';
import type RouteOption from '@/modules/common/models/option/route-option.ts';
import type DocumentOption from '@/modules/common/models/option/document-option.ts';
import DocumentTypeOption from '@/modules/common/models/option/document-type-option.ts';
import ProjectOption from '@/modules/common/models/option/project-option.ts';
import { StepCode } from '@/modules/document/document-master/enums/step-code.ts';
import { Parameter } from '@/modules/document/models/parameter.ts';
import { mapMaskFormValue } from '@/modules/document/document-master/utils/mask-value.ts';
import type { Mask, MaskFormData } from '@/modules/document/document-master/types/mask.ts';
import type { Steps } from '@/modules/document/document-master/types/step.ts';
import { UnspecifiedContractor } from '@/modules/document/models/unspecified-contractor.ts';
import type { ContractorFormData, ContractorStep } from '@/modules/document/document-master/types/contractor.ts';
import type { Deadline } from '@/modules/common/types/ui.ts';

type Route = {
  type: RouteType;
  route: RouteOption | null;
  document: DocumentOption | null;
  noRouteConfirm: boolean;
};

type Registration = {
  number: string;
  date: DateTime | null;
  auto: boolean;
  skip: boolean;
};

const createRegistration = (): Registration => ({
  number: '',
  date: DateTime.now(),
  auto: false,
  skip: false,
});

type State = {
  documentId: number | null;
  type: DocumentTypeOption;
  project: ProjectOption | null;
  mask: Mask;
  steps: Steps;
  content: string;
  comment: string;
  resolution: string;
  controller: Option<number> | null;
  deadline: Deadline;
  files: BaseFile[];
  links: DocumentCard[];
  clients: ClientCard[];
  route: Route;
  registration: Registration;
  registrationInitialized: boolean;
  contractors: ContractorStep[];
  skipUnassigned: boolean;
};

export const useMasterFormStore = defineStore(StoreId.MasterForm, {
  state: (): State => ({
    documentId: null,
    type: DocumentTypeOption.TYPE_DOCUMENT,
    project: null,
    mask: {
      parameters: [],
      formData: {},
    },
    steps: {
      barcode: null,
      files: null,
      links: null,
      clients: null,
      resolution: null,
      registration: null,
    },
    content: '',
    comment: '',
    resolution: '',
    controller: null,
    deadline: {
      type: DeadlineType.None,
      days: 1,
      date: DateTime.now().plus({ days: 1 }),
    },
    files: [],
    links: [],
    clients: [],
    route: {
      type: RouteType.Standard,
      route: null,
      document: null,
      noRouteConfirm: false,
    },
    registration: createRegistration(),
    registrationInitialized: false,
    contractors: [],
    skipUnassigned: false,
  }),
  getters: {
    requiredDocumentId(state) {
      if (state.documentId === null) {
        throw new Error('The document ID is not defined.');
      }
      return state.documentId;
    },
    getCount(state) {
      return (code: StepCode) => {
        switch (code) {
          case StepCode.Files:
            return state.files.length;
          case StepCode.Links:
            return state.links.length;
          case StepCode.Clients:
            return state.clients.length;
          default:
            return;
        }
      };
    },
    showProjectWarning(state) {
      const defaultProjectId = state.type.defaultProjectId;
      const projectId = state.project?.code;

      return (
        typeof defaultProjectId === 'number' &&
        defaultProjectId >= 0 &&
        typeof projectId === 'number' &&
        projectId !== defaultProjectId
      );
    },
    maskArrayRequest(state) {
      return state.mask.parameters.length
        ? state.mask.parameters.map((param) => mapMaskFormValue(state.mask.formData[param.number]))
        : null;
    },
    stepsWarnings(state) {
      return [
        state.steps.files === false && state.files.length && 'document.master.warning.steps.files',
        state.steps.links === false && state.links.length && 'document.master.warning.steps.documentLinks',
        state.steps.clients === false && state.clients.length && 'document.master.warning.steps.counterpartyLinks',
      ].filter(Boolean) as string[];
    },
    contractorAt: (state) => (index: number) => state.contractors[index],
    isControllerUser(state) {
      const userStore = useUserStore();
      return state.controller?.code === userStore.id;
    },
    hasSkippedContractors(state): boolean {
      return state.skipUnassigned && state.contractors.some((contractor) => !contractor.contractor);
    },
    isLaunchAvailable(state): boolean {
      return (
        this.isControllerUser &&
        state.route.type !== RouteType.None &&
        state.route.type !== RouteType.New &&
        !this.hasSkippedContractors
      );
    },
  },
  actions: {
    setDocumentId(documentId: number) {
      this.documentId = documentId;
    },
    setType(type: DocumentTypeOption) {
      if (this.type.fromApi && !type.fromApi) {
        return;
      }

      if (this.type.fromApi && this.type.code === type.code) {
        return;
      }

      this.type = type;
      this.mask = {
        parameters: [],
        formData: {},
      };
      this.links = this.links.map(LinkCard.resetLinkType);
      this.clients = this.clients.map(LinkCard.resetLinkType);
      this.route.type = type.defaultRouteType;
      this.registration = createRegistration();
      this.registrationInitialized = false;
    },
    setProject(project: ProjectOption | null) {
      if (this.project?.code !== project?.code) {
        this.project = project;
      }
    },
    setMaskParameters(parameters: Parameter[]) {
      this.mask.parameters = parameters;
    },
    setMaskFormData(formData: MaskFormData) {
      this.mask.formData = formData;
    },
    setContent(content: string) {
      this.content = content;
    },
    setComment(comment: string) {
      this.comment = comment;
    },
    setResolution(resolution: string) {
      this.resolution = resolution;
    },
    setDeadline(deadline: Deadline) {
      this.deadline = deadline;
    },
    setController(controller: Option<number> | null) {
      this.controller = controller;
    },
    setControllerUser() {
      const userStore = useUserStore();
      this.setController({
        code: userStore.required.id,
        label: userStore.required.name,
      });
    },
    setFiles(files: BaseFile[]) {
      if (files.length > 0 && this.steps.files === null) {
        this.steps.files = true;
      }
      this.files = files;
    },
    setLinks(links: DocumentCard[]) {
      if (links.length > 0 && this.steps.links === null) {
        this.steps.links = true;
      }
      this.links = links;
    },
    setClients(clients: ClientCard[]) {
      if (clients.length > 0 && this.steps.clients === null) {
        this.steps.clients = true;
      }
      this.clients = clients;
    },
    setSteps(steps: Steps) {
      this.steps = steps;
    },
    setRoute(route: Route) {
      this.route = route;
    },
    setRouteType(type: RouteType) {
      this.route = { ...this.route, type };
    },
    setRegistration(registration: Registration) {
      this.registration = registration;
    },
    initRegistration(auto: boolean) {
      if (this.registrationInitialized) {
        return;
      }

      this.registration = {
        ...this.registration,
        auto,
        number: auto ? (this.type.template ?? '') : this.registration.number,
      };
      this.registrationInitialized = true;
    },
    setUnspecifiedContractors(unspecifiedContractors: UnspecifiedContractor[]) {
      this.contractors = unspecifiedContractors.map((unspecifiedContractor): ContractorStep => ({
        unspecified: unspecifiedContractor,
        type: 'all',
        contractor: null,
        deadline: unspecifiedContractor.deadline,
        description: unspecifiedContractor.description,
        comment: unspecifiedContractor.comment,
      }));
    },
    updateContractor(index: number, formData: ContractorFormData) {
      const contractor = this.contractors[index];

      contractor.type = formData.type;
      contractor.contractor = formData.contractor;
      contractor.deadline = formData.deadline;
      contractor.description = formData.description;
    },
    setSkipUnassigned(value: boolean) {
      this.skipUnassigned = value;
    },
    clearContractorsFrom(index: number) {
      this.contractors.slice(index).forEach((contractor) => {
        contractor.contractor = null;
      });
    },
    async addFiles(raw: File[]) {
      const files = await ExternalFile.createList(raw);
      this.setFiles(mergeUniqueFiles(this.files as BaseFile[], files));
    },
  },
});
